import React, { useId, useState } from "react";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import {
  StyledModalContainer,
  StyledModalContent,
  StyledModalHeader,
  StyledModalBody,
  StyledModalFooter,
  StyledModalSubBody,
} from "./styled";
import Button from "../../components/Button";
import { DialogBoxProps } from "../../types/DialogBoxProps";
import { mainFont } from "../../utils/StyleHelpers";

const DialogBox = ({
  header,
  body,
  subBody,
  onCancel,
  isDelete,
  onSubmit,
  primaryButton,
  secondaryButton,
  width,
  disablePrimary = false,
  compact = false,
}: DialogBoxProps) => {
  const headerId = useId();
  const bodyId = useId();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (disablePrimary || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setIsSubmitting(false);
    }
  };

  const buttonStyle = {
    flex: 1,
    height: "56px",
    borderRadius: "8px",
    fontSize: compact ? "16px" : "20px",
    fontWeight: 500,
    ...(compact ? { padding: "1rem", whiteSpace: "nowrap" } : {}),
  } as const;

  return (
    <Dialog
      open
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && !isSubmitting) onCancel();
      }}
      aria-labelledby={headerId}
      aria-describedby={bodyId}
      fullWidth
      maxWidth={false}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
      PaperProps={{
        sx: {
          width: width || "600px",
          maxWidth: "calc(100% - 4rem)",
          borderRadius: "20px",
        },
      }}
    >
      <StyledModalContainer>
        <StyledModalContent>
          <DialogTitle
            id={headerId}
            component="div"
            sx={{
              padding: "40px 40px 0",
              fontFamily: mainFont,
              fontSize: "24px",
              fontWeight: 500,
              lineHeight: "29px",
            }}
          >
            <StyledModalHeader>{header}</StyledModalHeader>
          </DialogTitle>
          <DialogContent
            id={bodyId}
            style={{
              padding: "8px 40px 30px",
              color: "#666",
              fontFamily: mainFont,
              fontSize: "20px",
              fontWeight: 400,
              lineHeight: "28px",
            }}
            sx={{
              "&.MuiDialogContent-root:first-of-type": {
                paddingTop: "8px",
              },
            }}
          >
            <StyledModalBody>{body}</StyledModalBody>
            {subBody ? <StyledModalSubBody>{subBody}</StyledModalSubBody> : null}
          </DialogContent>
        </StyledModalContent>
        <DialogActions disableSpacing sx={{ padding: "0 40px 40px", gap: "12px" }}>
          <StyledModalFooter>
            <Button
              variant="contained"
              size="large"
              color="info"
              onClick={onCancel}
              disabled={isSubmitting}
              style={buttonStyle}
            >
              {secondaryButton}
            </Button>
            <Button
              variant="contained"
              size="large"
              color={isDelete ? "error" : "primary"}
              onClick={handleSubmit}
              disabled={disablePrimary || isSubmitting}
              aria-busy={isSubmitting}
              style={buttonStyle}
            >
              {isSubmitting ? <CircularProgress size={24} color="inherit" /> : primaryButton}
            </Button>
          </StyledModalFooter>
        </DialogActions>
      </StyledModalContainer>
    </Dialog>
  );
};

export default DialogBox;
